import { useState } from 'react';
import { Loader2, Play } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { toast } from '@/shared/lib/toast';

interface SendMessageFormData {
  message: string;
  instanceType: string;
}

interface OperationResult {
  totalProcessed: number;
  successCount: number;
  errorCount: number;
  messagesSent: number;
  errors?: Array<{
    clientName: string;
    error: string;
  }>;
}

interface ProgressInfo {
  current: number;
  total: number;
  currentClientName: string;
}

interface SendGroupMessagesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SendGroupMessagesModal({ isOpen, onClose }: SendGroupMessagesModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [operationResults, setOperationResults] = useState<OperationResult | null>(null);
  const [progressInfo, setProgressInfo] = useState<ProgressInfo | null>(null);
  const [formData, setFormData] = useState<SendMessageFormData>({
    message: '',
    instanceType: 'OPERACIONAL_ROBO',
  });

  const handleClose = () => {
    onClose();
    setFormData({ message: '', instanceType: 'OPERACIONAL_ROBO' });
    setShowResults(false);
    setOperationResults(null);
    setProgressInfo(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const executeOperation = async () => {
    if (!formData.message || !formData.instanceType) {
      toast.error('Erro', 'Preencha todos os campos');
      return;
    }

    setIsLoading(true);
    setShowResults(true);

    // Inicializar estatísticas
    setOperationResults({
      totalProcessed: 0,
      successCount: 0,
      errorCount: 0,
      messagesSent: 0,
    });

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
      const url = `${API_URL}/operations/send-group-messages/stream?message=${encodeURIComponent(formData.message)}&instanceType=${encodeURIComponent(formData.instanceType)}`;

      const eventSource = new EventSource(url, { withCredentials: true });
      let hasReceivedData = false;

      eventSource.onmessage = (event) => {
        try {
          hasReceivedData = true;
          const data = JSON.parse(event.data);

          if (data.type === 'init') {
            // Operação iniciada
          } else if (data.type === 'filtered') {
            setProgressInfo({
              current: 0,
              total: data.totalActive,
              currentClientName: '',
            });
          } else if (data.type === 'progress') {
            // Atualizar progresso
            setProgressInfo({
              current: data.current,
              total: data.total,
              currentClientName: data.clientName,
            });

            // Atualizar estatísticas em tempo real
            setOperationResults({
              totalProcessed: data.stats.totalProcessed,
              successCount: data.stats.successCount,
              errorCount: data.stats.errorCount,
              messagesSent: data.stats.messagesSent,
            });
          } else if (data.type === 'complete') {
            // Resultado final
            setOperationResults(data.result);
            setIsLoading(false);
            eventSource.close();
            toast.success('Sucesso', 'Operação concluída com sucesso!');
          } else if (data.type === 'error') {
            setIsLoading(false);
            eventSource.close();
            toast.error('Erro', data.error || 'Erro ao executar operação');
          }
        } catch (err) {
          console.error('Erro ao processar evento SSE:', err);
        }
      };

      eventSource.onerror = () => {
        if (!hasReceivedData) {
          toast.error(
            'Erro',
            'Não foi possível conectar ao servidor. Verifique se a API está rodando.'
          );
        } else {
          toast.error('Erro', 'Conexão interrompida com o servidor');
        }

        setIsLoading(false);
        eventSource.close();
      };
    } catch (error) {
      console.error('Erro ao executar operação:', error);
      setIsLoading(false);
      toast.error('Erro', 'Erro ao executar operação. Verifique os logs.');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogClose onClick={handleClose} />
        <DialogHeader>
          <DialogTitle>Enviar Mensagem para Grupos</DialogTitle>
          <DialogDescription>
            Esta operação irá enviar a mensagem para todos os grupos de clientes ativos
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-4">
          {!isLoading && !showResults ? (
            <>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                  Mensagem
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  placeholder="Digite a mensagem que será enviada para todos os grupos..."
                  value={formData.message}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="w-full px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  A mensagem será enviada exatamente como digitada, respeitando quebras de linha e
                  espaços
                </p>
              </div>

              <div>
                <label
                  htmlFor="instanceType"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Instância UAZAPI
                </label>
                <select
                  id="instanceType"
                  name="instanceType"
                  value={formData.instanceType}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="w-full px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="OPERACIONAL_ROBO">OPERACIONAL_ROBO</option>
                  <option value="OPERACIONAL_SAULO">OPERACIONAL_SAULO</option>
                  <option value="ADMIN">ADMIN</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Selecione a instância que será usada para enviar as mensagens
                </p>
              </div>

              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                  O que esta operação faz:
                </h4>
                <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
                  <li>Busca todos os clientes ativos no Notion com GroupId preenchido</li>
                  <li>Envia a mensagem para cada grupo de cliente</li>
                  <li>Aguarda 1.5 segundos entre cada envio para evitar bloqueios</li>
                  <li>Exibe estatísticas em tempo real durante o processo</li>
                </ul>
              </div>
            </>
          ) : showResults ? (
            <div className="space-y-4">
              {/* Barra de Progresso */}
              {isLoading && progressInfo && (
                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                      Processando...
                    </h4>
                    <span className="text-sm text-blue-700 dark:text-blue-300">
                      {progressInfo.current} de {progressInfo.total}
                    </span>
                  </div>

                  {/* Barra de progresso */}
                  <div className="w-full bg-blue-200 dark:bg-blue-900 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${(progressInfo.current / progressInfo.total) * 100}%` }}
                    ></div>
                  </div>

                  {progressInfo.currentClientName && (
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Processando: <strong>{progressInfo.currentClientName}</strong>
                    </p>
                  )}
                </div>
              )}

              {/* Estatísticas em Tempo Real */}
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-3">
                  {isLoading ? 'Processando...' : 'Operação Concluída!'}
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white dark:bg-green-900/20 rounded p-3">
                    <p className="text-sm text-green-700 dark:text-green-300">Total Processado</p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                      {operationResults?.totalProcessed || 0}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-green-900/20 rounded p-3">
                    <p className="text-sm text-green-700 dark:text-green-300">Mensagens Enviadas</p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                      {operationResults?.messagesSent || 0}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-green-900/20 rounded p-3">
                    <p className="text-sm text-green-700 dark:text-green-300">Sucessos</p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                      {operationResults?.successCount || 0}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-green-900/20 rounded p-3">
                    <p className="text-sm text-green-700 dark:text-green-300">Erros</p>
                    <p className="text-2xl font-bold text-red-900 dark:text-red-100">
                      {operationResults?.errorCount || 0}
                    </p>
                  </div>
                </div>
              </div>

              {operationResults?.errors && operationResults.errors.length > 0 && (
                <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4 max-h-60 overflow-y-auto">
                  <h4 className="text-sm font-semibold text-red-900 dark:text-red-100 mb-2">
                    Clientes com Erro:
                  </h4>
                  <ul className="space-y-2">
                    {operationResults.errors.map((error, index) => (
                      <li key={index} className="text-sm text-red-700 dark:text-red-300">
                        <strong>{error.clientName}:</strong> {error.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          {!isLoading && !showResults ? (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isLoading}>
                Cancelar
              </Button>
              <Button onClick={executeOperation} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Executar
                  </>
                )}
              </Button>
            </>
          ) : showResults ? (
            <>{!isLoading && <Button onClick={handleClose}>Fechar</Button>}</>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
